import { Box, Icon, Typography } from '@mui/material';
import { OrderHistoryDateCardSelection } from 'common/src/models/order.enums';
import { useCallback, useEffect, useRef, useState } from 'react';
import { debounce } from 'throttle-debounce';
import { CfContainedButton } from '../../../../cf-ui/buttons/CfContainedButton';
import { DeviceType, useBreakpoint } from '../../../../hooks/useBreakpoint';
import ProductListDiaryDateMenu from '../../../product-list-management/components/ProductListDiaryGrid/ProductListDiaryDateMenu';

type DiaryGridHeaderProps = {
  title: string;
  filterInputContainerRef: React.Ref<HTMLElement | null>;
  startDate: Date;
  endDate: Date;
  dateSelection: OrderHistoryDateCardSelection;
  onDateRangeChange: (startDate: Date, endDate: Date, selectionOption: OrderHistoryDateCardSelection) => void;
};

export default function DiaryGridHeader(props: DiaryGridHeaderProps): JSX.Element {
  const { title, filterInputContainerRef } = props;

  const { deviceType } = useBreakpoint();

  const [isDateMenuOpen, setIsDateMenuOpen] = useState<boolean>(false);
  const button = useRef<HTMLElement | null>(null);

  const getButtonText = (option: OrderHistoryDateCardSelection) => {
    let buttonText: string;

    switch (option) {
      case OrderHistoryDateCardSelection.Last7Days:
        buttonText = 'Last 7 days';
        break;
      case OrderHistoryDateCardSelection.Last30Days:
        buttonText = 'Last 30 days';
        break;
      case OrderHistoryDateCardSelection.Last90Days:
        buttonText = 'Last 90 days';
        break;
      case OrderHistoryDateCardSelection.Last12Months:
        buttonText = 'Last 12 months';
        break;
      case OrderHistoryDateCardSelection.YearToDate:
        buttonText = 'Year to date';
        break;
      default:
        buttonText = `${props.startDate.toLocaleDateString('en-US', {
          year: '2-digit',
          month: '2-digit',
          day: '2-digit',
        })} - ${props.endDate.toLocaleDateString('en-US', {
          year: '2-digit',
          month: '2-digit',
          day: '2-digit',
        })}`;
        break;
    }
    return buttonText;
  };

  const debouncedDateButtonClick = useCallback(
    debounce(100, true, () => setIsDateMenuOpen(true)),
    []
  );

  useEffect(() => {
    return () => debouncedDateButtonClick.cancel();
  }, [debouncedDateButtonClick]);

  const onDateChange = (startDate: Date, endDate: Date, selectionOption: OrderHistoryDateCardSelection) => {
    props.onDateRangeChange(startDate, endDate, selectionOption);
    if (selectionOption !== OrderHistoryDateCardSelection.Custom) setIsDateMenuOpen(false);
  };

  return (
    <>
      <Box
        sx={{
          width: '100%',
          display: 'grid',
          gap: 1,
          gridTemplateAreas: { xs: '"title" "date" "filter"', lg: '"title date filter"' },
          gridTemplateColumns: { xs: '1fr', lg: '1fr max-content min-content' },
        }}
      >
        <Typography variant='h3' sx={{ gridArea: 'title' }}>
          {title}
        </Typography>
        <Box ref={button} sx={{ gridArea: 'date' }}>
          <CfContainedButton onClick={() => debouncedDateButtonClick()} color='secondary' sx={{ gap: 1 }}>
            <Typography variant='paragraphBold'>{getButtonText(props.dateSelection)}</Typography>
            <Icon className='fa-calendar' />
          </CfContainedButton>
        </Box>
        <Box ref={filterInputContainerRef} sx={{ gridArea: 'filter' }} />
      </Box>
      {isDateMenuOpen && (
        <ProductListDiaryDateMenu
          isOpen={isDateMenuOpen}
          startDate={props.startDate}
          endDate={props.endDate}
          currentOption={props.dateSelection}
          onClose={() => setIsDateMenuOpen(!isDateMenuOpen)}
          onChange={onDateChange}
          anchorRef={isDateMenuOpen && button.current ? button.current : undefined}
          showDrawer={deviceType !== DeviceType.Desktop}
          data-testid='oh-date-menu'
        />
      )}
    </>
  );
}
